/**
 * Řazení hledání v paměti adminu podle stupně shody (vize F9.4, řez 09): každý seznam a výběr
 * adminu, který filtruje řádky podle hledaného textu v paměti, filtruje i řadí touto funkcí,
 * takže „Bali" najde Bali první, ne lokalitu, která Bali jen zmiňuje v popisu.
 *
 * Stupeň shody pro ořezaný dotaz malými písmeny (bez odstraňování diakritiky):
 * 0 = název se rovná dotazu, 1 = název začíná dotazem, 2 = název dotaz obsahuje,
 * 3 = dotaz je jen v jiném prohledávaném poli; `null` = bez shody (řádek se nezobrazí).
 * Při stejném stupni jde destinace na úrovni země před lokalitou, pak dosavadní řazení
 * seznamu (`tieBreak`).
 *
 * Bydlí v `packages/shared`, protože admin nemá vlastní testovací runtime (precedent
 * `linkPickerItems`). Signatury jsou Kontrakt řezu 09 (bod 4); těla a test píše část K4.
 */

export type SearchMatchRank = 0 | 1 | 2 | 3;

/** Stupeň shody řádku s dotazem; `null` = dotaz není v názvu ani v jiném poli. */
export function searchMatchRank(
  query: string,
  name: string | null | undefined,
  ...other: Array<string | null | undefined>
): SearchMatchRank | null {
  const q = query.trim().toLowerCase();
  if (!q) return 0;
  const n = (name ?? "").trim().toLowerCase();
  if (n === q) return 0;
  if (n.startsWith(q)) return 1;
  if (n.includes(q)) return 2;
  if (other.some((f) => f?.toLowerCase().includes(q))) return 3;
  return null;
}

/**
 * Filtruje i řadí řádky podle stupně shody s dotazem; prázdný dotaz = všechny řádky seřazené
 * jen `tieBreak`. Vstup nemění: `rows` bývá `data.data` z keše React Query a řazení na místě
 * by přeházelo pořadí všem ostatním čtenářům téže keše.
 *
 * Pořadí klíčů je F9.4: stupeň shody, pak úroveň země (jen když volající `isCountryLevel`
 * předá; predikát je sdílený `isCountryLevel` z `routes.ts`, modul si vlastní nezakládá),
 * pak zvolené řazení seznamu (`tieBreak`), nakonec vstupní pořadí. Poslední klíč dělá
 * řazení stabilní i s `tieBreak = () => 0`, takže seznam bez vlastního řazení drží pořadí
 * z API.
 */
export function sortBySearchMatch<T>(
  rows: T[],
  query: string,
  opts: {
    name: (row: T) => string | null | undefined;
    other: (row: T) => Array<string | null | undefined>;
    isCountryLevel?: (row: T) => boolean;
    tieBreak: (a: T, b: T) => number;
  },
): T[] {
  const { name, other, isCountryLevel, tieBreak } = opts;
  const q = query.trim();
  if (!q) {
    return rows
      .map((row, index) => ({ row, index }))
      .sort((a, b) => tieBreak(a.row, b.row) || a.index - b.index)
      .map((w) => w.row);
  }
  const wrapped: Array<{ row: T; rank: SearchMatchRank; country: boolean; index: number }> = [];
  rows.forEach((row, index) => {
    const rank = searchMatchRank(q, name(row), ...other(row));
    if (rank === null) return;
    wrapped.push({ row, rank, country: isCountryLevel ? isCountryLevel(row) : false, index });
  });
  return wrapped
    .sort(
      (a, b) =>
        a.rank - b.rank ||
        Number(b.country) - Number(a.country) ||
        tieBreak(a.row, b.row) ||
        a.index - b.index,
    )
    .map((w) => w.row);
}
